import { FC, useCallback, useMemo, useRef, useState, useEffect } from "react";
import TimingSlider from "./TimingSlider";
import moment from "moment";
import { useTranslation } from "react-i18next";

interface MatchTimingsGraphProps {
    intensityData: number[];
    sliderPositions: number[];
    onSliderChange: (index: number, newPosition: number) => void;
    activityStartTime: number;
    activityEndTime: number;
}

const MatchTimingsGraph: FC<MatchTimingsGraphProps> = ({
    intensityData,
    sliderPositions,
    onSliderChange,
    activityStartTime,
    activityEndTime,
}) => {
    const { t } = useTranslation();
    const SLIDER_LABELS = [
        t("model.WatchFixture.firstHalfDisplayName"),
        t("model.WatchFixture.halfTimeDisplayName"),
        t("model.WatchFixture.secondHalfDisplayName"),
        t("fixture.FixtureSummaryMatch.fullTime"),
    ];
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerHeight, setContainerHeight] = useState(0);

    useEffect(() => {
        const updateHeight = () => {
            if (containerRef.current) {
                setContainerHeight(containerRef.current.clientHeight);
            }
        };
        updateHeight();
        window.addEventListener("resize", updateHeight);
        return () => window.removeEventListener("resize", updateHeight);
    }, []);

    const totalDuration = activityEndTime - activityStartTime;

    const getTimeLabel = useCallback(
        (position: number): string => {
            const timestamp = activityStartTime + position * totalDuration;
            return moment(timestamp).format("HH:mm");
        },
        [activityStartTime, totalDuration]
    );

    const getDurationLabel = useCallback(
        (sliderIndex: number): string | undefined => {
            if (sliderIndex === 1) {
                // HALF TIME: show 1st half duration
                const firstHalfMs =
                    (sliderPositions[1] - sliderPositions[0]) * totalDuration;
                const mins = Math.round(firstHalfMs / 60000);
                return `(${mins} ${t("general.mins")})`;
            }
            if (sliderIndex === 3) {
                // FULL TIME: show 2nd half duration
                const secondHalfMs =
                    (sliderPositions[3] - sliderPositions[2]) * totalDuration;
                const mins = Math.round(secondHalfMs / 60000);
                return `(${mins} ${t("general.mins")})`;
            }
            return undefined;
        },
        [sliderPositions, totalDuration]
    );

    // Build SVG path for the intensity wave
    const svgPath = useMemo(() => {
        if (intensityData.length === 0) return "";

        const center = 50;
        const maxAmplitude = 31.5; // 70% of 45

        const pathPoints = intensityData.map((value, i) => {
            const y = (i / intensityData.length) * 100;
            const amplitude = value * maxAmplitude;
            return {
                y,
                leftX: center - amplitude,
                rightX: center + amplitude,
            };
        });

        let path = `M ${pathPoints[0].leftX} ${pathPoints[0].y}`;

        for (let i = 1; i < pathPoints.length; i++) {
            if (i < pathPoints.length - 1) {
                const curr = pathPoints[i];
                const next = pathPoints[i + 1];
                path += ` Q ${curr.leftX} ${curr.y}, ${(curr.leftX + next.leftX) / 2} ${(curr.y + next.y) / 2}`;
            } else {
                path += ` L ${pathPoints[i].leftX} ${pathPoints[i].y}`;
            }
        }

        for (let i = pathPoints.length - 1; i >= 0; i--) {
            if (i > 0) {
                const curr = pathPoints[i];
                const prev = pathPoints[i - 1];
                path += ` Q ${curr.rightX} ${curr.y}, ${(curr.rightX + prev.rightX) / 2} ${(curr.y + prev.y) / 2}`;
            } else {
                path += ` L ${pathPoints[i].rightX} ${pathPoints[i].y}`;
            }
        }

        path += " Z";
        return path;
    }, [intensityData]);

    return (
        <div className="match-timings-graph" ref={containerRef}>
            {/* SVG intensity wave */}
            <svg
                className="match-timings-svg"
                preserveAspectRatio="none"
                viewBox="0 0 100 100"
            >
                <defs>
                    <linearGradient
                        id="timingsWaveGradient"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="0%"
                    >
                        <stop offset="0%" stopColor="#0eaa0b" stopOpacity="0.6" />
                        <stop offset="50%" stopColor="#0eaa0b" stopOpacity="0.1" />
                        <stop offset="100%" stopColor="#0eaa0b" stopOpacity="0.6" />
                    </linearGradient>
                </defs>
                {svgPath && (
                    <path
                        d={svgPath}
                        fill="url(#timingsWaveGradient)"
                        stroke="#0eaa0b"
                        strokeWidth="2"
                        vectorEffect="non-scaling-stroke"
                        opacity="0.7"
                    />
                )}
                {/*<line*/}
                {/*    x1="50"*/}
                {/*    y1="0"*/}
                {/*    x2="50"*/}
                {/*    y2="100"*/}
                {/*    stroke="#0eaa0b"*/}
                {/*    strokeWidth="1"*/}
                {/*    opacity="0.2"*/}
                {/*/>*/}
            </svg>

            {/* Playing period overlays */}
            <div
                className="match-timings-playing-overlay"
                style={{
                    top: `${sliderPositions[0] * 100}%`,
                    height: `${(sliderPositions[1] - sliderPositions[0]) * 100}%`,
                }}
            />
            <div
                className="match-timings-playing-overlay"
                style={{
                    top: `${sliderPositions[2] * 100}%`,
                    height: `${(sliderPositions[3] - sliderPositions[2]) * 100}%`,
                }}
            />

            {/* Slider overlays */}
            {sliderPositions.map((pos, i) => (
                <TimingSlider
                    key={i}
                    index={i}
                    label={SLIDER_LABELS[i]}
                    position={pos}
                    timeLabel={getTimeLabel(pos)}
                    durationLabel={getDurationLabel(i)}
                    onPositionChange={onSliderChange}
                    containerHeight={containerHeight}
                />
            ))}
        </div>
    );
};

export default MatchTimingsGraph;
